import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const VideoCallPanel = ({ isActive, participants, onEndCall, onToggleMute, onToggleVideo, onToggleScreenShare }) => {
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [showParticipants, setShowParticipants] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    let interval;
    if (isActive) {
      interval = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isActive]);

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes?.toString()?.padStart(2, '0')}:${secs?.toString()?.padStart(2, '0')}`;
    }
    return `${minutes}:${secs?.toString()?.padStart(2, '0')}`;
  };

  const handleMuteToggle = () => {
    setIsMuted(!isMuted);
    onToggleMute(!isMuted);
  };

  const handleVideoToggle = () => {
    setIsVideoOff(!isVideoOff);
    onToggleVideo(!isVideoOff);
  };

  const handleScreenShareToggle = () => {
    setIsScreenSharing(!isScreenSharing);
    onToggleScreenShare(!isScreenSharing);
  };

  if (!isActive) return null;

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-black/50 text-white">
        <div className="flex items-center space-x-4">
          <h3 className="font-medium">Video Call</h3>
          <span className="text-sm opacity-75">{formatDuration(callDuration)}</span>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowParticipants(!showParticipants)}
            className="text-white hover:bg-white/20"
          >
            <Icon name="Users" size={20} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onEndCall}
            className="text-white hover:bg-white/20"
          >
            <Icon name="X" size={20} />
          </Button>
        </div>
      </div>
      {/* Video Grid */}
      <div className="flex-1 relative">
        {isScreenSharing ? (
          <div className="w-full h-full bg-gray-900 flex items-center justify-center">
            <div className="text-center text-white">
              <Icon name="Monitor" size={64} className="mx-auto mb-4 opacity-50" />
              <p className="text-lg">Screen sharing active</p>
              <p className="text-sm opacity-75">Your screen is being shared with participants</p>
            </div>
          </div>
        ) : (
          <div className={`grid h-full gap-2 p-4 ${
            participants?.length <= 2 ? 'grid-cols-1' :
            participants?.length <= 4 ? 'grid-cols-2': 'grid-cols-3'
          }`}>
            {participants?.map((participant) => (
              <div key={participant?.id} className="relative bg-gray-800 rounded-lg overflow-hidden">
                {participant?.videoEnabled ? (
                  <div className="w-full h-full bg-gray-700 flex items-center justify-center">
                    <Icon name="User" size={48} className="text-gray-400" />
                  </div>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    {participant?.avatar ? (
                      <Image
                        src={participant?.avatar}
                        alt={participant?.name}
                        className="w-24 h-24 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-24 h-24 bg-primary rounded-full flex items-center justify-center">
                        <Icon name="User" size={32} color="white" />
                      </div>
                    )}
                  </div>
                )}
                
                {/* Participant Info */}
                <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between">
                  <span className="text-white text-sm font-medium bg-black/50 px-2 py-1 rounded">
                    {participant?.name}
                  </span>
                  <div className="flex items-center space-x-1">
                    {participant?.isMuted && (
                      <Icon name="MicOff" size={16} className="text-red-400" />
                    )}
                    {!participant?.videoEnabled && (
                      <Icon name="VideoOff" size={16} className="text-red-400" />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Controls */}
      <div className="flex items-center justify-center space-x-4 p-6 bg-black/50">
        <Button
          variant={isMuted ? "destructive" : "secondary"}
          size="lg"
          onClick={handleMuteToggle}
          iconName={isMuted ? "MicOff" : "Mic"}
          className="rounded-full w-12 h-12"
        />
        
        <Button
          variant={isVideoOff ? "destructive" : "secondary"}
          size="lg"
          onClick={handleVideoToggle}
          iconName={isVideoOff ? "VideoOff" : "Video"}
          className="rounded-full w-12 h-12"
        />
        
        <Button
          variant={isScreenSharing ? "default" : "secondary"}
          size="lg"
          onClick={handleScreenShareToggle}
          iconName="Monitor"
          className="rounded-full w-12 h-12"
        />
        
        <Button
          variant="destructive"
          size="lg"
          onClick={onEndCall}
          iconName="PhoneOff"
          className="rounded-full w-12 h-12"
        />
      </div>
      {/* Participants Panel */}
      {showParticipants && (
        <div className="absolute right-0 top-0 bottom-0 w-80 bg-card border-l border-border shadow-elevated">
          <div className="p-4 border-b border-border">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-foreground">Participants ({participants?.length})</h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowParticipants(false)}
              >
                <Icon name="X" size={20} />
              </Button>
            </div>
          </div>
          
          <div className="p-4 space-y-3">
            {participants?.map((participant) => (
              <div key={participant?.id} className="flex items-center space-x-3">
                {participant?.avatar ? (
                  <Image
                    src={participant?.avatar}
                    alt={participant?.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                    <Icon name="User" size={16} color="white" />
                  </div>
                )}
                
                <div className="flex-1">
                  <p className="font-medium text-foreground">{participant?.name}</p>
                  <p className="text-sm text-muted-foreground">{participant?.role}</p>
                </div>
                
                <div className="flex items-center space-x-1">
                  {participant?.isMuted && (
                    <Icon name="MicOff" size={16} className="text-error" />
                  )}
                  {!participant?.videoEnabled && (
                    <Icon name="VideoOff" size={16} className="text-error" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoCallPanel;